// VIDEO-EDITING — Shared Remotion layer types.
//
// Self-contained TS interfaces consumed by the pure Remotion composition
// primitives below. The zod schemas in
//   - core/src/platform/providers/video-edit/spec.ts
//   - core/src/platform/providers/overlays/schema.ts
// are the trust boundary; these interfaces mirror them as plain TS so
// both the BE renderer (which imports these files directly) and the FE
// player (which imports them via the `@wiser/remotion-layers` alias)
// can share the exact same component source without pulling in zod.
//
// When you change a field on a zod schema, update the matching interface
// here in the same change.

export const CAPTION_PRESET_IDS = [
  "trust-bar",
  "hero-headline",
  "word-by-word-accent",
  "two-line-conversational",
  "quote-card",
  "sticker-bar",
  "hook-body-stack",
  "caption-lite",
] as const;

export type CaptionPresetId = (typeof CAPTION_PRESET_IDS)[number];

export type CaptionAlignment = "left" | "center" | "right";

export type CaptionAnimation =
  | "fade"
  | "pop"
  | "wordReveal"
  | "slideUp"
  | "static";

export interface CaptionPreset {
  id: CaptionPresetId;
  name: string;
  /** 0..1 — vertical position from top. */
  positionY: number;
  alignment: CaptionAlignment;
  fontWeight: number;
  /** Fraction of player height. */
  fontSize: number;
  /** Text color — hex or rgba. */
  color: string;
  /** Optional active-word color (word-by-word-accent). */
  accentColor?: string;
  backgroundColor?: string;
  /** CSS-style "<px> <color>" outline. */
  textStroke?: string;
  textShadow?: string;
  uppercase?: boolean;
  letterSpacing?: number;
  maxLines: number;
  animation: CaptionAnimation;
  inset: number;
}

export interface CustomCaptionStyle {
  positionY?: number;
  fontSize?: number;
  color?: string;
  fontFamily?: string;
  fontWeight?: number;
  /** Active-word highlight color. Overrides preset.accentColor on
   *  word-by-word-accent presets. The "__rainbow__" sentinel is rendered
   *  as a 6-color per-word cycle. */
  accentColor?: string;
}

export interface CaptionWord {
  start: number;
  end: number;
  word: string;
}

export interface CaptionSegment {
  start: number;
  end: number;
  text: string;
  /** Optional per-word timing — required for word-by-word-accent. */
  words?: CaptionWord[];
}

export interface CaptionsSpec {
  presetId: CaptionPresetId | null;
  customStyle: CustomCaptionStyle | null;
  activeLanguage: string;
  segments: CaptionSegment[];
}

export type OverlayKind =
  | "text-box"
  | "pill-hook"
  | "lower-third"
  | "sticker-image";

export type OverlayAnim =
  | "none"
  | "fade"
  | "pop"
  | "slide-up"
  | "slide-down"
  | "slide-left"
  | "slide-right";

export type OverlayAnchor =
  | "tl"
  | "tc"
  | "tr"
  | "ml"
  | "mc"
  | "mr"
  | "bl"
  | "bc"
  | "br";

export const OVERLAY_FONT_FAMILIES = [
  "Inter",
  "Vazirmatn",
  "Cairo",
  "Noto Sans Hebrew",
  "Noto Sans CJK",
  "Bebas Neue",
  "Anton",
  "Archivo Black",
  "Alfa Slab One",
  "Abril Fatface",
  "Bungee",
  "Oswald",
  "Montserrat",
  "Poppins",
  "Raleway",
  "DM Sans",
  "Playfair Display",
  "Merriweather",
  "Lora",
  "Roboto Slab",
  "DM Serif Display",
  "Lobster",
  "Pacifico",
  "Dancing Script",
  "Caveat",
  "Permanent Marker",
  "Bangers",
  "Comic Neue",
  "Fredoka",
  "JetBrains Mono",
] as const;

export type OverlayFontFamily = (typeof OVERLAY_FONT_FAMILIES)[number];

export type OverlaySourceKind =
  | "manual"
  | "generated"
  | "clone-source"
  | "upload"
  | "image-asset";

export interface OverlayStyle {
  color?: string;
  backgroundColor?: string;
  fontSize?: number;
  fontWeight?: number;
  fontFamily?: OverlayFontFamily;
  textAlign?: "left" | "center" | "right";
  paddingX?: number;
  paddingY?: number;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  uppercase?: boolean;
  letterSpacing?: number;
  rotation?: number;
  opacity?: number;
  shadow?: {
    dx: number;
    dy: number;
    blur: number;
    color: string;
  };
}

export interface OverlayAsset {
  url: string;
  storageKey: string;
  mimeType: string;
  w: number;
  h: number;
}

export interface OverlayMotionPoint {
  timeSec: number;
  x: number;
  y: number;
  scale?: number;
  opacity?: number;
}

export interface OverlaySource {
  kind?: OverlaySourceKind;
  actionId?: string;
  managed?: boolean;
  cloneSourceArtifactId?: string;
  sourceTrackId?: string;
  confidence?: number;
}

export interface Overlay {
  id: string;
  kind: OverlayKind;
  role?: string;
  lane?: string;
  x: number;
  y: number;
  w: number;
  h: number;
  anchor: OverlayAnchor;
  startSec: number;
  endSec: number;
  text?: string;
  style?: OverlayStyle;
  asset?: OverlayAsset;
  animIn?: OverlayAnim;
  animOut?: OverlayAnim;
  animDurMs?: number;
  motionPath?: OverlayMotionPoint[];
  source?: OverlaySource;
}

export interface OverlayTranslationEntry {
  id: string;
  text: string;
}

export interface OverlayTranslation {
  language: string;
  overlays: OverlayTranslationEntry[];
}

export interface SourceSpec {
  videoUrl: string;
  mimeType: string;
  storageKey: string;
}

export interface DimensionsSpec {
  width: number;
  height: number;
  durationSec: number;
}

export const CLIP_TRANSITION_KINDS = [
  "fade",
  "slide-left",
  "slide-right",
  "slide-up",
  "slide-down",
  "wipe-left",
  "wipe-right",
  "wipe-up",
  "wipe-down",
  "wipe-top-left",
  "wipe-top-right",
  "wipe-bottom-left",
  "wipe-bottom-right",
  "flip-left",
  "flip-right",
  "flip-up",
  "flip-down",
  "clock-wipe",
  "iris",
  "zoom-blur",
  "zoom-in-out",
] as const;

export type ClipTransitionKind = (typeof CLIP_TRANSITION_KINDS)[number];

// ---------------------------------------------------------------------------
// Clips — the project timeline. `clips[]` is an ordered list of trimmed
// media ranges the renderer lays end-to-end (see the b-roll ripple-insert
// plan). `kind:'source'` clips reference the original voiceover video;
// `kind:'broll'` clips are video inserts whose own audio can be muted or
// mixed; `kind:'still'` clips are generated image inserts. Splitting a source
// clip yields two `source` clips that share one asset URL with adjacent trim
// ranges — no file is split; the renderer's per-clip trim handles the sub-range.
//
// Mirrors `ClipSchema` in core/src/platform/providers/video-edit/spec.ts.
// ---------------------------------------------------------------------------

export type ClipKind = "source" | "broll" | "still";
export type ClipSourceAudioMode = "none" | "continue";

export interface Clip {
  /** Stable — React key + ripple-op identity. */
  id: string;
  kind: ClipKind;
  /** Playable media URL. */
  url: string;
  /** Storage path — '' allowed for legacy synthesized clips. */
  storageKey: string;
  mimeType: string;
  /** In-point within the asset. */
  trimStartSec: number;
  /** Out-point within the asset (> trimStartSec). */
  trimEndSec: number;
  /** True drops this clip's own embedded audio. Still clips must stay muted. */
  muted: boolean;
  /** Gain for this clip's own embedded audio when `muted` is false. Defaults to 1. */
  volume?: number;
  /**
   * For inserted media, optionally keep the original source video's audio
   * playing underneath this visual clip. `continue` is inferred from the
   * adjacent source split point when no explicit source-audio trim is stored.
   */
  sourceAudioMode?: ClipSourceAudioMode;
  /** Gain for source audio carried underneath inserted media. Defaults to 1. */
  sourceAudioVolume?: number;
  /**
   * Full duration of the underlying media asset (seconds). Optional —
   * stamped on `broll` clips at upload so the trim handles know how far
   * the out-point may extend back after a trim-in. Absent on legacy clips
   * and synthesized `source` clips (the editor never trims `source`).
   */
  assetDurationSec?: number;
  /**
   * Crossfade duration (seconds) with the PREVIOUS clip. Ignored when the
   * clip is at index 0 — no preceding clip to crossfade from — or when
   * 0/undefined (hard cut). `transitionInKind` selects the Remotion
   * presentation template; omitted means `fade`. Rendered via
   * `@remotion/transitions`: the
   * transition window is shared between this clip's first `transitionInSec`
   * seconds and the previous clip's last `transitionInSec` seconds, so the
   * combined timeline length is `Σdur − ΣtransitionInSec`. Capped at write
   * time to leave at least one rendered frame in each adjacent sequence
   * (TransitionSeries rejects a transition that consumes either sequence).
   */
  transitionInSec?: number;
  transitionInKind?: ClipTransitionKind;
}

export type BrollSourceKind = "stock" | "generative" | "upload";

export interface BrollSource {
  kind?: BrollSourceKind;
  /** Groups brolls applied by one apply-action for idempotent replace. */
  actionId?: string;
  /** The propose-step proposal this broll came from (regenerate handle). */
  proposalId?: string;
  aiPrompt?: string;
  provider?: string;
  searchQuery?: string;
  attributionLine?: string | null;
}

// ---------------------------------------------------------------------------
// B-rolls — an OVERLAY-style layer rendered ON TOP of the source for the
// `[startSec, endSec]` window. Unlike a `kind:'broll'` Clip, a BrollClip
// never splices the source track: the narration video+audio play straight
// through underneath, so lip-sync can never drift. This replaces the legacy
// cover-mode clip-splice (which relied on a fragile length-preservation
// invariant that broke and desynced the tail with 2+ brolls). Mirrors
// `BrollClipSchema` in core/src/platform/providers/video-edit/spec.ts.
// ---------------------------------------------------------------------------

export interface BrollClip {
  /** Stable — React key + idempotent-replace identity. */
  id: string;
  url: string;
  storageKey: string;
  mimeType: string;
  /** Timeline window this broll covers (seconds, on top of the source). */
  startSec: number;
  endSec: number;
  /** In/out point within the broll asset (seconds). */
  trimStartSec: number;
  trimEndSec: number;
  /** Full underlying asset duration (seconds) — trim-handle metadata. */
  assetDurationSec?: number;
  /** Drop the broll's own audio (default true — narration plays underneath). */
  muted: boolean;
  /** Gain for the broll's own audio when not muted. Defaults to 1. */
  volume?: number;
  /** Fade in/out duration (seconds) at the broll edges. 0/undefined = hard cut. */
  transitionSec?: number;
  transitionKind?: ClipTransitionKind;
  /** 0..1 — broll opacity over the source. Defaults to 1 (full cover). */
  opacity?: number;
  source?: BrollSource;
}

// ---------------------------------------------------------------------------
// Background music — a single royalty-free track laid under the whole
// composition. In the FE <Player> the composition plays it (looped, gain-
// reduced) so the editor preview is audible; on the BE export it is NOT
// baked by the renderer — the export pipeline strips it and mixes the track
// in with ffmpeg afterwards (robust looping + fades + ducking via gain),
// so the renderer (Remotion or ffmpeg fallback) stays music-agnostic.
// Mirrors `MusicSpecSchema` in core/src/platform/providers/video-edit/spec.ts.
// ---------------------------------------------------------------------------

export interface MusicSpec {
  /** Catalog track id (slug). */
  trackId: string;
  /** Playable / downloadable track URL (re-hosted royalty-free mp3). */
  trackUrl: string;
  /** Music gain in dB relative to dialogue/ambient (≤ 0). Defaults to -18. */
  gainDb: number;
}

export interface VideoEditSpec {
  projectId: string;
  source: SourceSpec | null;
  /** Primary timeline — renderer uses this when non-empty, else `source`. */
  clips: Clip[];
  dimensions: DimensionsSpec | null;
  captions: CaptionsSpec;
  overlays: Overlay[];
  overlayTranslations: OverlayTranslation[];
  /** B-roll cutaways rendered on top of the source (never splices it). */
  brolls: BrollClip[];
  /** Optional background-music track laid under the whole composition. */
  music?: MusicSpec | null;
}

export const OVERLAY_ANIM_DEFAULT_MS = 250;

export const RTL_LANGUAGES = new Set([
  "fa",
  "ar",
  "he",
  "ur",
  "ps",
  "sd",
  "fa-ir",
  "ar-sa",
  "he-il",
]);

export function isRtlLanguage(tag: string | null | undefined): boolean {
  if (!tag) return false;
  const code = tag.toLowerCase();
  if (RTL_LANGUAGES.has(code)) return true;
  const base = code.split("-")[0];
  return RTL_LANGUAGES.has(base);
}
